/* Validation: two sets of values, side by side: the header fields (one row per field) and the monthly line items.
 *
 *   Set 1 "Reported + new"  what was reported (the report's own figures, for every month up to its as-at date)
 *                           plus the new data for months the report does not cover yet
 *   Set 2 "All new data"    the freshly calculated figures from the validation table, for every month
 *
 * so the difference shows how the new data compares with what was reported. Months nobody has reported yet are the same
 * in both sets by definition (status "new"). Pure functions; the table itself is loaded by store/validationStore.js. */
import S from './core.js';
import { HEADER_FIELDS } from './constants.js';
import { MONTH_NAMES } from './periods.js';

/** A difference up to this size is rounding, not a real difference. */
export const TOLERANCE = { money: 0.005, int: 0.5, pct: 0.05 };
export const SECTION_NAMES = { site_detail: 'Site detail', financials: 'Financials', by_site: 'By site' };
export const STATUS = { match: 'Match', differs: 'Differs', missing: 'Not in new data', new: 'New period' };

const lineIdOf = (r) => `${r.section}|${r.site_name || ''}|${r.line_item_code}`;
const monthOf = (r) => Number(String(r.period_start).slice(5, 7)) - 1;
const labelFor = (site, item) => (site ? `${site} - ${String(item).replace(/ by Site\s*$/, '')}` : item);

/** The latest tables are copies of settlement_report / settlement_report_figure, so they can hold several as-at versions of one report.
 * Only the latest version is compared: per report id and year, the rows with the most recent as_at_date. Rows without an as_at_date
 * (the sample data) are kept as they are. (The reported side is already the latest as-at of each report: see latestPerReport.) */
export function pickVersion(rows) {
  const key = (r) => `${r.settlement_report_id}|${r.report_year}`;
  const newest = new Map();
  rows.forEach((r) => { if (r.as_at_date && (!newest.has(key(r)) || r.as_at_date > newest.get(key(r)))) newest.set(key(r), r.as_at_date); });
  return rows.filter((r) => !r.as_at_date || r.as_at_date === newest.get(key(r)));
}

/** Years that have new data. */
export const yearsOf = (rows) => [...new Set(rows.map((r) => Number(String(r.period_start).slice(0, 4))))];
/** The last month (0-11) that has new data in this year, or null. */
export function lastNewMonth(rows, year) {
  const months = rows.filter((r) => r.report_year === year).map(monthOf);
  return months.length ? Math.max(...months) : null;
}

/** entries: [{ report, figures }] for the year (latest as-at of each report); newRows: validation_figure rows.
 * -> rows [{ key, id, partner, monthIdx, month, section, sectionName, label, kind, reported, source, fresh, diff, pct, status }] */
export function buildComparison(entries, newRows, year, monthIdx) {
  const reports = new Map(entries.map((e) => [e.report.settlement_report_id, e]));
  const fresh = new Map();                                       // 'id|month|lineId' -> row
  const freshLines = new Map();                                  // report id -> Map(lineId -> { section, site, code, item }) in first-seen order
  pickVersion(newRows).forEach((r) => {
    if (r.report_year !== year || monthOf(r) > monthIdx || monthOf(r) < 0) return;
    fresh.set(`${r.settlement_report_id}|${monthOf(r)}|${lineIdOf(r)}`, r);
    if (!freshLines.has(r.settlement_report_id)) freshLines.set(r.settlement_report_id, new Map());
    const m = freshLines.get(r.settlement_report_id);
    if (!m.has(lineIdOf(r))) m.set(lineIdOf(r), { section: r.section, site: r.site_name || null, code: r.line_item_code, item: r.line_item });
  });

  const out = [];
  [...new Set([...reports.keys(), ...freshLines.keys()])].sort().forEach((id) => {
    const entry = reports.get(id);
    const lines = new Map();                                     // lineId -> { section, site, code, item, cells }  (reported first, in report order)
    if (entry) {
      const byId = new Map(S.linesOf(entry.figures).map((L) => [L.id, L]));
      S.buildLayout(entry.report, entry.figures).pages.forEach((pg) => pg.rows.forEach((row) => {
        if (row.k === 'grid' && byId.has(row.id) && !lines.has(row.id)) lines.set(row.id, byId.get(row.id));
      }));
      byId.forEach((L, k) => { if (!lines.has(k)) lines.set(k, L); });
    }
    (freshLines.get(id) || new Map()).forEach((L, k) => { if (!lines.has(k)) lines.set(k, { ...L, cells: [] }); });

    const partner = entry ? (entry.report.client_name || entry.report.company_name || '') : '';
    for (let mo = 0; mo <= monthIdx; mo++) {
      const reportedMonth = !!entry && S.periodMeta(mo, year).start <= entry.report.as_at_date;
      lines.forEach((L, lineId) => {
        const cell = L.cells[mo];
        const r = cell && cell.value !== null && cell.value !== undefined ? cell.value : null;
        const f = fresh.get(`${id}|${mo}|${lineId}`);
        const n = f ? f.value : null;
        if (r === null && n === null) return;
        const kind = S.kindOf(L.code);
        const tol = TOLERANCE[kind] ?? TOLERANCE.money;
        const base = { scope: 'line', key: `${id}/${mo}/${lineId}`, id, partner, monthIdx: mo, month: MONTH_NAMES[mo], section: L.section, sectionName: SECTION_NAMES[L.section] || L.section,
          label: labelFor(L.site, L.item), kind };
        if (!(reportedMonth || r !== null)) {                    // not reported yet: Set 1 takes the new data
          out.push({ ...base, reported: n, source: 'New', fresh: n, diff: 0, pct: null, status: 'new' });
          return;
        }
        if (n === null) {                                        // reported, but the new data has nothing for it
          if (Math.abs(r) <= tol) return;
          out.push({ ...base, reported: r, source: 'Reported', fresh: null, diff: null, pct: null, status: 'missing' });
          return;
        }
        const diff = n - (r ?? 0);
        const same = Math.abs(diff) <= tol;
        out.push({ ...base, reported: r, source: 'Reported', fresh: n, diff: same ? 0 : diff, pct: !same && r ? (diff / Math.abs(r)) * 100 : null, status: same ? 'match' : 'differs' });
      });
    }
  });
  return out;
}

/* ---- header ---- */
const norm = (v) => (v === null || v === undefined ? '' : String(v).replace(/\s+/g, ' ').trim());
const asObject = (v) => { if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return {}; } } return v && typeof v === 'object' && !Array.isArray(v) ? v : {}; };
const headerValues = (rep) => {                                  // [[key, label, text]] for every header column, then the extra fields
  const out = HEADER_FIELDS.map(([k, label]) => [k, label, norm(rep[k])]);
  const extra = asObject(rep.extra ?? rep.extra_fields);
  Object.keys(extra).forEach((k) => out.push([`extra:${k}`, k, norm(typeof extra[k] === 'object' ? JSON.stringify(extra[k]) : extra[k])]));
  return out;
};

/** Header values (settlement_report) against settlement_report_latest, one row per field.
 * entries: [{ report, ... }]; newReports: validation_report rows (null when the source has none -> nothing to compare). */
export function buildHeaderComparison(entries, newReports, year) {
  if (!Array.isArray(newReports)) return [];
  const mine = pickVersion(newReports.filter((r) => Number(r.report_year) === year));
  const fresh = new Map(mine.map((r) => [r.settlement_report_id, r]));
  const reported = new Map(entries.map((e) => [e.report.settlement_report_id, e.report]));
  const out = [];
  [...new Set([...reported.keys(), ...fresh.keys()])].sort().forEach((id) => {
    const rep = reported.get(id), now = fresh.get(id);
    const partner = rep ? (rep.client_name || rep.company_name || '') : '';
    const a = new Map(rep ? headerValues(rep).map(([k, l, v]) => [k, { l, v }]) : []);
    const b = new Map(now ? headerValues(now).map(([k, l, v]) => [k, { l, v }]) : []);
    [...new Set([...a.keys(), ...b.keys()])].forEach((k) => {
      const r = a.get(k)?.v || '', n = b.get(k)?.v || '';
      if (!r && !n) return;
      const base = { scope: 'header', key: `${id}/header/${k}`, id, partner, monthIdx: null, month: 'Header', section: 'header', sectionName: 'Header', label: (a.get(k) || b.get(k)).l, kind: 'text' };
      if (!rep) return out.push({ ...base, reported: n, source: 'New', fresh: n, diff: 0, pct: null, status: 'new' });
      const status = !now || !n ? 'missing' : r === n ? 'match' : 'differs';
      out.push({ ...base, reported: r || null, source: 'Reported', fresh: now && n ? n : null, diff: status === 'match' ? 0 : null, pct: null, status });
    });
  });
  return out;
}

/** Header and line items together: one list for the table, the figures strip and the filters. */
export const buildValidation = (entries, newRows, newReports, year, monthIdx) =>
  [...buildHeaderComparison(entries, newReports, year), ...buildComparison(entries, newRows, year, monthIdx)];

/** scope: 'all', 'header' or 'line'. show: 'differences' (differs + not in new data), 'all', 'matches' or 'new'. The other filters are "contains", ignoring case. */
export function filterComparison(rows, { partner = '', reportId = '', month = 'all', line = '', show = 'differences', scope = 'all' } = {}) {
  const p = partner.trim().toLowerCase(), id = reportId.trim().toLowerCase(), l = line.trim().toLowerCase();
  const wanted = { differences: ['differs', 'missing'], matches: ['match'], new: ['new'] }[show];
  return rows.filter((r) => (scope === 'all' || r.scope === scope) && (!wanted || wanted.includes(r.status)) && (!p || r.partner.toLowerCase().includes(p)) && (!id || r.id.toLowerCase().includes(id))
    && (month === 'all' || (r.monthIdx !== null && r.monthIdx === Number(month))) && (!l || r.label.toLowerCase().includes(l) || r.sectionName.toLowerCase().includes(l)));
}

/** Counts for the figures strip. `compared` = reported values that were checked against the new data. */
export function tally(rows) {
  const t = { total: rows.length, match: 0, differs: 0, missing: 0, new: 0 };
  rows.forEach((r) => { t[r.status] += 1; });
  t.compared = t.match + t.differs + t.missing;
  return t;
}

export const signed = (kind, v, fmt) => (v === null || v === undefined ? '' : `${v > 0 ? '+' : ''}${fmt(kind, v)}`);
